import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { BookOpen, Loader2 } from 'lucide-react';
import { documentService } from '@/services/kbService';

interface SaveToKBModalProps {
  open: boolean;
  onClose: () => void;
  content: string;
  onSuccess?: (documentName: string) => void;
}

export const SaveToKBModal: React.FC<SaveToKBModalProps> = ({
  open,
  onClose,
  content,
  onSuccess,
}) => {
  const [title, setTitle] = useState('');
  const [category, setCategory] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Reset state and auto-generate title when modal opens
  useEffect(() => {
    if (open && content) {
      const firstLine = content.trim().split('\n')[0].replace(/^#+\s*/, '');
      const autoTitle = firstLine.length > 60 ? firstLine.slice(0, 60) + '...' : firstLine;
      setTitle(autoTitle);
      setCategory('');
      setError('');
      setSaving(false);
    }
  }, [open, content]);


  const handleSave = async () => {
    if (!title.trim()) {
      setError('Title is required');
      return;
    }

    setSaving(true);
    setError('');

    try {
      // Check for duplicate title before saving
      const titleExists = await documentService.checkTitleExists(title.trim());
      if (titleExists) {
        setError('A document with this title already exists in the Knowledge Base');
        setSaving(false);
        return;
      }

      const result = await documentService.saveResponseToKB(
        content,
        title.trim(),
        'General',
        category || undefined
      );

      if (result.status === 'success') {
        setTitle('');
        setCategory('');
        onSuccess?.(result.document_name || '');
        onClose();
      } else {
        setError(result.message || 'Failed to save');
      }
    } catch (err: any) {
      // Handle Frappe error formats: _server_messages (JSON array), exception, or message
      let errorMsg = 'Failed to save to Knowledge Base';
      const data = err?.response?.data;
      if (data?._server_messages) {
        try {
          const msgs = JSON.parse(data._server_messages);
          errorMsg = typeof msgs[0] === 'string' ? JSON.parse(msgs[0]).message || msgs[0] : msgs[0];
        } catch {
          errorMsg = data._server_messages;
        }
      } else if (data?.exception) {
        errorMsg = data.exception.split('\n').pop() || data.exception;
      } else if (data?.message) {
        errorMsg = typeof data.message === 'string' ? data.message : data.message?.message || errorMsg;
      } else if (err?.message) {
        errorMsg = err.message;
      }
      setError(errorMsg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="dark bg-background text-foreground sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <BookOpen className="w-5 h-5" />
            Save to Knowledge Base
          </DialogTitle>
          <DialogDescription>
            This response will be saved as a document and indexed for future searches.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-4">
          {/* Title */}
          <div className="grid gap-2">
            <Label>Title *</Label>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Document title"
              className={error && !title.trim() ? 'border-red-500/60' : ''}
            />
          </div>

          {/* Category */}
          <div className="grid gap-2">
            <Label>Category (optional)</Label>
            <Input
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              placeholder="e.g., Pricing, Safety, Best Practices"
            />
          </div>

          {/* Preview */}
          <div className="grid gap-2">
            <Label className="text-xs text-muted-foreground">Content preview</Label>
            <div className="rounded-md border p-3 max-h-24 overflow-y-auto text-sm text-muted-foreground">
              {content.slice(0, 300)}{content.length > 300 ? '...' : ''}
            </div>
          </div>

          {/* Error */}
          {error && (
            <p className="text-sm text-red-400">{error}</p>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={saving}
          >
            Cancel
          </Button>
          {/* No bg-brand-primary override: --brand-primary is the light-theme
              navy (#0B1F3A) and has no .dark counterpart, so inside this dark
              dialog it rendered a navy fill under near-black
              `text-primary-foreground` text. The default variant's `bg-primary`
              is theme-aware and matches every other primary button. */}
          <Button
            onClick={handleSave}
            disabled={saving || !title.trim()}
          >
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              'Save to KB'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SaveToKBModal;
